import { useEffect, useState } from "react";
import { Trash2, Mail, Phone, Building2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, asArray } from "@/lib/api";

const STATUS_OPTIONS = ["new", "read", "responded"];

const AdminInquiries = () => {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () =>
    api
      .get("/admin/inquiries", { params: { status: filter } })
      .then((r) => setList(asArray(r.data)))
      .catch(() => setList([]));

  useEffect(() => {
    load();
  }, [filter]);

  const updateStatus = async (i, status) => {
    try {
      await api.put(`/admin/inquiries/${i.id}`, { status });
      toast.success("Status diperbarui");
      if (open?.id === i.id) setOpen({ ...open, status });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    try {
      await api.put(`/admin/inquiries/${open.id}`, { notes });
      toast.success("Catatan tersimpan");
      setOpen({ ...open, notes });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus inquiry ini?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Dihapus");
      if (open?.id === id) setOpen(null);
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const openDetail = (i) => {
    setOpen(i);
    setNotes(i.notes || "");
    if (i.status === "new") updateStatus(i, "read");
  };

  return (
    <div className="space-y-6" data-testid="admin-inquiries-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="kt-eyebrow">Komunikasi</div>
          <h1 className="font-display text-3xl font-bold mt-2">Inquiries</h1>
        </div>
        <div className="flex gap-2">
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-2 text-xs uppercase tracking-[0.14em] font-semibold border ${
                filter === s
                  ? "bg-[#18181B] text-white border-[#18181B]"
                  : "bg-white text-[#18181B] border-[#E4E4E7] hover:border-[#18181B]"
              }`}
              data-testid={`inquiry-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="kt-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-[#52525B]">
              <th className="px-4 py-3">Tanggal</th>
              <th className="px-4 py-3">Nama</th>
              <th className="px-4 py-3">Email / Telp</th>
              <th className="px-4 py-3">Perusahaan</th>
              <th className="px-4 py-3">Layanan</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr
                key={i.id}
                className="border-b border-[#E4E4E7] last:border-0 hover:bg-[#FAFAFA] cursor-pointer"
                onClick={() => openDetail(i)}
                data-testid={`admin-inquiry-row-${i.id}`}
              >
                <td className="px-4 py-3 font-mono-kt text-xs text-[#52525B]">
                  {new Date(i.created_at).toLocaleDateString("id-ID")}
                </td>
                <td className="px-4 py-3 font-medium">{i.name}</td>
                <td className="px-4 py-3 text-[#52525B]">
                  <div>{i.email}</div>
                  <div className="text-xs">{i.phone}</div>
                </td>
                <td className="px-4 py-3 text-[#52525B]">{i.company || "—"}</td>
                <td className="px-4 py-3 text-[#52525B]">{i.service_type}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 ${
                      i.status === "new"
                        ? "bg-[#DC2626] text-white"
                        : i.status === "responded"
                        ? "bg-[#0369A1] text-white"
                        : "bg-[#F4F4F5] text-[#18181B]"
                    }`}
                  >
                    {i.status}
                  </span>
                </td>
                <td
                  className="px-4 py-3 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => remove(i.id)}
                    className="p-2 hover:bg-[#FEE2E2] text-[#DC2626]"
                    data-testid={`admin-inquiry-delete-${i.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[#52525B]">
                  Tidak ada inquiry.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4"
          data-testid="inquiry-detail-modal"
        >
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#E4E4E7]">
              <div>
                <div className="kt-eyebrow">Inquiry #{open.id}</div>
                <h2 className="font-display text-xl font-bold mt-1">
                  {open.name}
                </h2>
              </div>
              <button
                onClick={() => setOpen(null)}
                className="p-2 hover:bg-[#F4F4F5]"
                data-testid="inquiry-detail-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-[#DC2626]" />
                  <a href={`mailto:${open.email}`} className="hover:underline">
                    {open.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-[#DC2626]" />
                  <span>{open.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2 md:col-span-2">
                  <Building2 size={16} className="text-[#DC2626]" />
                  <span>
                    {open.company || "—"} · Layanan:{" "}
                    <span className="font-semibold">{open.service_type}</span>
                  </span>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#52525B] mb-2">
                  Pesan
                </div>
                <div className="p-4 bg-[#FAFAFA] border border-[#E4E4E7] text-sm whitespace-pre-wrap">
                  {open.message}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#52525B] mb-2">
                  Status
                </div>
                <div className="flex gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(open, s)}
                      className={`px-3 py-2 text-xs uppercase tracking-[0.14em] font-semibold border ${
                        open.status === s
                          ? "bg-[#DC2626] text-white border-[#DC2626]"
                          : "bg-white text-[#18181B] border-[#E4E4E7]"
                      }`}
                      data-testid={`inquiry-status-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#52525B] mb-2">
                  Catatan Internal
                </div>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full border border-[#E4E4E7] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0369A1] focus:ring-1 focus:ring-[#0369A1]"
                  data-testid="inquiry-notes-input"
                />
                <button
                  onClick={saveNotes}
                  className="kt-btn-secondary mt-3"
                  data-testid="inquiry-save-notes"
                >
                  Simpan Catatan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;
